import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import type { Device } from '@types';
import {
  getEffectiveHeartRateData,
  parseHeartRateData,
} from '../../../shared/hr-data';
import { EVENT_PARSE_BATCH, FETCH_CONCURRENCY } from '../constants';
import {
  fetchSessionConfiguration,
  getCachedSessionConfiguration,
} from '../queries/prefetchSessionConfigurations';
import { configurationQueryKey } from '../queries/keys';
import type { SpectralWorkspaceSession } from '../types';
import type { SessionRepairStatus } from '../../session-repair/types';
import { deriveSessionRepairFieldsFromConfiguration } from '../../session-repair/utils/deriveRepairStatusFromConfiguration';
import { resolveSessionCompletedAt } from '../utils/resolveSessionCompletedAt';
import { resolveSessionSensorFields } from '../utils/sessionDevice';
import {
  completeBulkSessionLoadToast,
  hideBulkSessionLoadToast,
  showBulkSessionLoadToast,
  updateBulkSessionLoadToast,
} from '../utils/sessionLoadNotifications';

export type HydrateSessionEntry = {
  sessionId: number;
  patientCode?: string | null;
  sessionDate?: string | Date | null;
  completedAt?: string | Date | null;
  deviceName?: string | null;
  deviceSensorName?: string | null;
  hasHrData?: boolean;
  hasHrDataRepaired?: boolean;
};

type HydrateSessionsOptions = {
  bulkLoad?: boolean;
  deferFetch?: boolean;
  deferEventParse?: boolean;
};

const deriveRepairStatusFromLite = (
  entry: Pick<HydrateSessionEntry, 'hasHrData' | 'hasHrDataRepaired'>
): SessionRepairStatus => {
  if (entry.hasHrData === false) return 'no_hr_data';
  if (entry.hasHrDataRepaired) return 'repaired_perfect';
  return 'not_analyzed';
};

const buildMetadataReadySession = (
  entry: HydrateSessionEntry,
  cached?: DeviceConfigurationApi,
  lookup?: Map<string, Device>
): SpectralWorkspaceSession => {
  const sensorFields = resolveSessionSensorFields(cached, lookup);

  return {
    sessionId: entry.sessionId,
    patientCode: entry.patientCode ?? cached?.patientCode,
    sessionDate:
      entry.sessionDate ?? cached?.started_at ?? cached?.created_at ?? null,
    completedAt:
      entry.completedAt ?? resolveSessionCompletedAt(cached) ?? null,
    deviceName: entry.deviceName ?? sensorFields.deviceName,
    deviceSensorName: entry.deviceSensorName ?? sensorFields.deviceSensorName,
    hasHrData: entry.hasHrData ?? true,
    hasHrDataRepaired:
      entry.hasHrDataRepaired ?? Boolean(cached?.hrData_repaired),
    repairStatus: deriveRepairStatusFromLite(entry),
    status: 'ready',
  };
};

const enrichSessionWithEvents = (
  session: SpectralWorkspaceSession,
  data: DeviceConfigurationApi,
  entry: Pick<
    HydrateSessionEntry,
    | 'patientCode'
    | 'sessionDate'
    | 'completedAt'
    | 'hasHrData'
    | 'hasHrDataRepaired'
    | 'deviceName'
    | 'deviceSensorName'
  >,
  lookup?: Map<string, Device>
): SpectralWorkspaceSession => {
  const events = getEffectiveHeartRateData(data);
  const rawEvents = parseHeartRateData(data.hrData);
  // After full config load, trust actual HR payload over lite metadata defaults.
  const hasHrData = entry.hasHrData === false ? false : events.length > 0;
  const hasHrDataRepaired =
    entry.hasHrDataRepaired ?? Boolean(data.hrData_repaired);
  const repairFields = deriveSessionRepairFieldsFromConfiguration({
    hrData: data.hrData,
    hrData_repaired: data.hrData_repaired,
    hasHrData,
    hasHrDataRepaired,
  });
  const sensorFields = resolveSessionSensorFields(data, lookup);

  return {
    ...session,
    patientCode: entry.patientCode ?? session.patientCode,
    sessionDate:
      entry.sessionDate ??
      session.sessionDate ??
      data.started_at ??
      data.created_at ??
      null,
    completedAt:
      entry.completedAt ??
      session.completedAt ??
      resolveSessionCompletedAt(data),
    deviceName:
      entry.deviceName ?? session.deviceName ?? sensorFields.deviceName,
    deviceSensorName:
      entry.deviceSensorName ??
      session.deviceSensorName ??
      sensorFields.deviceSensorName,
    hasHrData,
    hasHrDataRepaired,
    events,
    rawEvents,
    repairStatus: repairFields.repairStatus,
    duplicateCount: repairFields.duplicateCount,
    gapCount: repairFields.gapCount,
    status: 'ready',
  };
};

const buildSessionFromConfiguration = (
  sessionId: number,
  data: DeviceConfigurationApi,
  entry: Pick<
    HydrateSessionEntry,
    | 'patientCode'
    | 'sessionDate'
    | 'completedAt'
    | 'hasHrData'
    | 'hasHrDataRepaired'
    | 'deviceName'
    | 'deviceSensorName'
  >,
  lookup?: Map<string, Device>
): SpectralWorkspaceSession =>
  enrichSessionWithEvents(
    buildMetadataReadySession({ sessionId, ...entry }, data, lookup),
    data,
    entry,
    lookup
  );

const buildLoadingSession = (
  entry: HydrateSessionEntry
): SpectralWorkspaceSession => {
  const hasHrData = entry.hasHrData ?? true;
  const hasHrDataRepaired = entry.hasHrDataRepaired ?? false;

  return {
    sessionId: entry.sessionId,
    patientCode: entry.patientCode,
    sessionDate: entry.sessionDate ?? null,
    completedAt: entry.completedAt ?? null,
    deviceName: entry.deviceName ?? null,
    deviceSensorName: entry.deviceSensorName ?? null,
    hasHrData,
    hasHrDataRepaired,
    repairStatus: hasHrData ? 'analyzing' : 'no_hr_data',
    status: 'loading',
  };
};

const buildErrorSession = (
  entry: HydrateSessionEntry,
  errorMessage: string
): SpectralWorkspaceSession => {
  const hasHrData = entry.hasHrData ?? true;
  const hasHrDataRepaired = entry.hasHrDataRepaired ?? false;

  return {
    sessionId: entry.sessionId,
    patientCode: entry.patientCode,
    sessionDate: entry.sessionDate ?? null,
    completedAt: entry.completedAt ?? null,
    deviceName: entry.deviceName ?? null,
    deviceSensorName: entry.deviceSensorName ?? null,
    hasHrData,
    hasHrDataRepaired,
    repairStatus: hasHrData ? 'analysis_failed' : 'no_hr_data',
    status: 'error',
    error: errorMessage,
  };
};

const scheduleBackgroundWork = (work: () => void) => {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(work);
    return;
  }

  window.setTimeout(work, 0);
};

const scheduleDeferredWork = (work: () => void) => {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => work(), { timeout: 500 });
    return;
  }

  scheduleBackgroundWork(work);
};

export const useWorkspaceSessions = (
  sensorDeviceLookup?: Map<string, Device>
) => {
  const queryClient = useQueryClient();
  const sensorLookupRef = useRef(sensorDeviceLookup);
  sensorLookupRef.current = sensorDeviceLookup;
  const [sessions, setSessions] = useState<
    Record<number, SpectralWorkspaceSession>
  >({});
  const queueRef = useRef<number[]>([]);
  const inFlightRef = useRef<Set<number>>(new Set());
  const activeCountRef = useRef(0);
  const sessionsRef = useRef(sessions);
  const entriesRef = useRef<Record<number, HydrateSessionEntry>>({});
  const bulkLoadRef = useRef(false);
  const bulkPendingRef = useRef(0);
  const bulkTotalRef = useRef(0);
  const bulkCompletedRef = useRef(0);
  const eventParseRunRef = useRef(0);
  const repairStatusRunRef = useRef(0);
  const pendingEventParseRef = useRef<
    Array<{
      entry: HydrateSessionEntry;
      cached: DeviceConfigurationApi;
    }>
  >([]);

  // Intentionally do NOT sync sessionsRef from render state — that races with
  // async setSessions updaters and can leave stale "loading" entries.

  const scheduleProgressiveRepairStatus = useCallback(
    (
      items: Array<{
        entry: HydrateSessionEntry;
        cached: DeviceConfigurationApi;
      }>
    ) => {
      const runId = ++repairStatusRunRef.current;
      let index = 0;

      const updateBatch = () => {
        if (repairStatusRunRef.current !== runId) {
          return;
        }

        const batch = items.slice(index, index + EVENT_PARSE_BATCH);
        index += EVENT_PARSE_BATCH;

        if (batch.length === 0) {
          return;
        }

        setSessions(current => {
          const next = { ...current };

          batch.forEach(({ entry, cached }) => {
            const existing = next[entry.sessionId];
            if (!existing) {
              return;
            }

            const hasHrData = entry.hasHrData ?? true;
            const hasHrDataRepaired =
              entry.hasHrDataRepaired ?? Boolean(cached.hrData_repaired);
            const repairFields = deriveSessionRepairFieldsFromConfiguration({
              hrData: cached.hrData,
              hrData_repaired: cached.hrData_repaired,
              hasHrData,
              hasHrDataRepaired,
            });

            const sensorFields = resolveSessionSensorFields(
              cached,
              sensorLookupRef.current
            );

            next[entry.sessionId] = {
              ...existing,
              hasHrData,
              hasHrDataRepaired,
              deviceName:
                entry.deviceName ??
                existing.deviceName ??
                sensorFields.deviceName,
              deviceSensorName:
                entry.deviceSensorName ??
                existing.deviceSensorName ??
                sensorFields.deviceSensorName,
              repairStatus: repairFields.repairStatus,
              duplicateCount: repairFields.duplicateCount,
              gapCount: repairFields.gapCount,
            };
          });

          return next;
        });

        if (index < items.length) {
          scheduleBackgroundWork(updateBatch);
        }
      };

      scheduleBackgroundWork(updateBatch);
    },
    []
  );

  const scheduleProgressiveEventParse = useCallback(
    (
      items: Array<{
        entry: HydrateSessionEntry;
        cached: DeviceConfigurationApi;
      }>
    ) => {
      const runId = ++eventParseRunRef.current;
      let index = 0;

      const parseBatch = () => {
        if (eventParseRunRef.current !== runId) {
          return;
        }

        const batch = items.slice(index, index + EVENT_PARSE_BATCH);
        index += EVENT_PARSE_BATCH;

        if (batch.length === 0) {
          return;
        }

        setSessions(current => {
          const next = { ...current };

          batch.forEach(({ entry, cached }) => {
            const existing = next[entry.sessionId];
            if (!existing || (existing.events?.length ?? 0) > 0) {
              return;
            }

            next[entry.sessionId] = enrichSessionWithEvents(
              existing,
              cached,
              entry,
              sensorLookupRef.current
            );
          });

          return next;
        });

        if (index < items.length) {
          scheduleBackgroundWork(parseBatch);
        }
      };

      scheduleBackgroundWork(parseBatch);
    },
    []
  );

  const fetchSession = useCallback(
    async (sessionId: number) => {
      const entry = entriesRef.current[sessionId];
      if (!entry) {
        return;
      }

      try {
        const data = await fetchSessionConfiguration(queryClient, sessionId);
        const built = buildSessionFromConfiguration(
          sessionId,
          data,
          entry,
          sensorLookupRef.current
        );

        setSessions(current => {
          const next = {
            ...current,
            [sessionId]: built,
          };
          sessionsRef.current = next;
          return next;
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Impossibile caricare i dati HR.';

        setSessions(current => {
          const next = {
            ...current,
            [sessionId]: buildErrorSession(entry, errorMessage),
          };
          sessionsRef.current = next;
          return next;
        });
      } finally {
        inFlightRef.current.delete(sessionId);
        if (bulkLoadRef.current) {
          bulkPendingRef.current = Math.max(0, bulkPendingRef.current - 1);
          bulkCompletedRef.current += 1;
          const completed = Math.min(
            bulkCompletedRef.current,
            bulkTotalRef.current
          );
          updateBulkSessionLoadToast(completed, bulkTotalRef.current);

          // Complete from counters only — sessionsRef can lag; never block toast on it.
          if (
            bulkPendingRef.current === 0 &&
            inFlightRef.current.size === 0 &&
            bulkCompletedRef.current >= bulkTotalRef.current
          ) {
            bulkLoadRef.current = false;
            completeBulkSessionLoadToast(bulkTotalRef.current);

            // Reconcile any stale "loading" rows left by render/ref races.
            setSessions(current => {
              let changed = false;
              const next = { ...current };
              Object.values(current).forEach(session => {
                if (session.status !== 'loading') return;
                const entry = entriesRef.current[session.sessionId];
                if (!entry) return;
                const cached = queryClient.getQueryData<DeviceConfigurationApi>(
                  configurationQueryKey(String(session.sessionId))
                );
                if (cached) {
                  next[session.sessionId] = buildSessionFromConfiguration(
                    session.sessionId,
                    cached,
                    entry,
                    sensorLookupRef.current
                  );
                  changed = true;
                } else {
                  next[session.sessionId] = buildErrorSession(
                    entry,
                    'Impossibile caricare i dati HR.'
                  );
                  changed = true;
                }
              });
              if (!changed) return current;
              sessionsRef.current = next;
              return next;
            });
          }
        }
      }
    },
    [queryClient]
  );

  const processQueue = useCallback(() => {
    while (
      activeCountRef.current < FETCH_CONCURRENCY &&
      queueRef.current.length > 0
    ) {
      const sessionId = queueRef.current.shift();
      if (sessionId == null) break;

      const entry = sessionsRef.current[sessionId];
      if (entry?.status === 'ready' || entry?.status === 'error') continue;
      if (inFlightRef.current.has(sessionId)) continue;

      inFlightRef.current.add(sessionId);
      activeCountRef.current += 1;
      void fetchSession(sessionId).finally(() => {
        activeCountRef.current -= 1;
        processQueue();
      });
    }
  }, [fetchSession]);

  const enqueueFetch = useCallback(
    (sessionIds: number[]) => {
      const queuedOrInFlight = new Set([
        ...queueRef.current,
        ...inFlightRef.current,
      ]);
      const pending = sessionIds.filter(sessionId => {
        if (queuedOrInFlight.has(sessionId)) return false;
        const entry = sessionsRef.current[sessionId];
        return entry?.status !== 'ready' && entry?.status !== 'error';
      });

      if (pending.length === 0) {
        if (
          bulkLoadRef.current &&
          bulkPendingRef.current === 0 &&
          inFlightRef.current.size === 0
        ) {
          hideBulkSessionLoadToast();
        }
        return;
      }

      if (bulkLoadRef.current) {
        bulkPendingRef.current += pending.length;
      }

      queueRef.current.push(...pending);
      processQueue();
    },
    [processQueue]
  );

  const hydrateSessions = useCallback(
    (entries: HydrateSessionEntry[], options: HydrateSessionsOptions = {}) => {
      if (entries.length === 0) {
        sessionsRef.current = {};
        entriesRef.current = {};
        queueRef.current = [];
        inFlightRef.current.clear();
        bulkLoadRef.current = false;
        bulkPendingRef.current = 0;
        bulkTotalRef.current = 0;
        bulkCompletedRef.current = 0;
        hideBulkSessionLoadToast();
        setSessions({});
        return;
      }

      eventParseRunRef.current += 1;

      const targetIds = new Set(entries.map(entry => entry.sessionId));
      const nextEntries: Record<number, HydrateSessionEntry> = {};
      const nextSessions: Record<number, SpectralWorkspaceSession> = {};
      const fetchIds: number[] = [];
      const eventParseQueue: Array<{
        entry: HydrateSessionEntry;
        cached: DeviceConfigurationApi;
      }> = [];

      entries.forEach(entry => {
        nextEntries[entry.sessionId] = entry;
      });

      Object.entries(sessionsRef.current).forEach(([sessionId, session]) => {
        if (!targetIds.has(Number(sessionId))) {
          return;
        }

        const entry = nextEntries[Number(sessionId)];
        if (!entry) {
          return;
        }

        // Keep in-memory ready/loading sessions across re-hydrate (RQ cache may be stale).
        if (session.status === 'ready' && (session.events?.length ?? 0) > 0) {
          const cached = getCachedSessionConfiguration(
            queryClient,
            Number(sessionId)
          );

          if (cached) {
            const repairFields = deriveSessionRepairFieldsFromConfiguration({
              hrData: cached.hrData,
              hrData_repaired: cached.hrData_repaired,
              hasHrData: entry.hasHrData ?? session.hasHrData,
              hasHrDataRepaired:
                entry.hasHrDataRepaired ?? session.hasHrDataRepaired,
            });

            const sensorFields = resolveSessionSensorFields(
              cached,
              sensorLookupRef.current
            );

            nextSessions[Number(sessionId)] = {
              ...session,
              patientCode: entry.patientCode ?? session.patientCode,
              sessionDate: entry.sessionDate ?? session.sessionDate,
              completedAt:
                entry.completedAt ??
                session.completedAt ??
                resolveSessionCompletedAt(cached),
              deviceName:
                entry.deviceName ??
                session.deviceName ??
                sensorFields.deviceName,
              deviceSensorName:
                entry.deviceSensorName ??
                session.deviceSensorName ??
                sensorFields.deviceSensorName,
              hasHrData: entry.hasHrData ?? session.hasHrData,
              hasHrDataRepaired:
                entry.hasHrDataRepaired ?? session.hasHrDataRepaired,
              rawEvents:
                session.rawEvents ?? parseHeartRateData(cached.hrData),
              repairStatus: repairFields.repairStatus,
              duplicateCount: repairFields.duplicateCount,
              gapCount: repairFields.gapCount,
            };
            return;
          }

          nextSessions[Number(sessionId)] = {
            ...session,
            patientCode: entry.patientCode ?? session.patientCode,
            sessionDate: entry.sessionDate ?? session.sessionDate,
            completedAt: entry.completedAt ?? session.completedAt,
            deviceName: entry.deviceName ?? session.deviceName,
            hasHrData: entry.hasHrData ?? session.hasHrData,
            hasHrDataRepaired:
              entry.hasHrDataRepaired ?? session.hasHrDataRepaired,
          };
          return;
        }

        if (session.status === 'loading' || session.status === 'error') {
          nextSessions[Number(sessionId)] = {
            ...session,
            patientCode: entry.patientCode ?? session.patientCode,
            sessionDate: entry.sessionDate ?? session.sessionDate,
            completedAt: entry.completedAt ?? session.completedAt,
            deviceName: entry.deviceName ?? session.deviceName,
            hasHrData: entry.hasHrData ?? session.hasHrData,
            hasHrDataRepaired:
              entry.hasHrDataRepaired ?? session.hasHrDataRepaired,
          };
        }
      });

      entries.forEach(entry => {
        const existing = nextSessions[entry.sessionId];
        if (
          existing?.status === 'ready' &&
          (existing.events?.length ?? 0) > 0
        ) {
          return;
        }

        if (existing?.status === 'loading') {
          const queuedOrInFlight =
            queueRef.current.includes(entry.sessionId) ||
            inFlightRef.current.has(entry.sessionId);
          if (!queuedOrInFlight) {
            fetchIds.push(entry.sessionId);
          }
          return;
        }

        const cached = getCachedSessionConfiguration(
          queryClient,
          entry.sessionId
        );
        if (cached) {
          nextSessions[entry.sessionId] = buildMetadataReadySession(
            entry,
            cached,
            sensorLookupRef.current
          );
          if ((existing?.events?.length ?? 0) === 0) {
            eventParseQueue.push({ entry, cached });
          }
          return;
        }

        nextSessions[entry.sessionId] = buildLoadingSession(entry);
        fetchIds.push(entry.sessionId);
      });

      entriesRef.current = nextEntries;
      sessionsRef.current = nextSessions;
      setSessions(nextSessions);

      if (eventParseQueue.length > 0) {
        if (options.deferEventParse) {
          pendingEventParseRef.current = eventParseQueue;
          scheduleProgressiveRepairStatus(eventParseQueue);
        } else {
          scheduleProgressiveEventParse(eventParseQueue);
        }
      }

      if (fetchIds.length === 0) {
        if (!bulkLoadRef.current) {
          hideBulkSessionLoadToast();
        }
        return;
      }

      const wasBulkLoading = bulkLoadRef.current;

      if (wasBulkLoading) {
        // Keep stable toast total; only enqueue ids not already queued/in-flight.
        const startFetch = () => enqueueFetch(fetchIds);
        if (options.deferFetch) {
          scheduleDeferredWork(startFetch);
        } else {
          startFetch();
        }
        return;
      }

      bulkLoadRef.current = true;
      bulkTotalRef.current = fetchIds.length;
      bulkCompletedRef.current = 0;
      bulkPendingRef.current = 0;
      showBulkSessionLoadToast(fetchIds.length);

      const startFetch = () => enqueueFetch(fetchIds);

      if (options.deferFetch) {
        scheduleDeferredWork(startFetch);
      } else {
        startFetch();
      }
    },
    [
      enqueueFetch,
      queryClient,
      scheduleProgressiveEventParse,
      scheduleProgressiveRepairStatus,
    ]
  );

  const parsePendingSessionEvents = useCallback(() => {
    const pending = pendingEventParseRef.current.filter(
      ({ entry }) =>
        (sessionsRef.current[entry.sessionId]?.events?.length ?? 0) === 0
    );

    pendingEventParseRef.current = [];

    if (pending.length === 0) {
      return;
    }

    scheduleProgressiveEventParse(pending);
  }, [scheduleProgressiveEventParse]);

  useEffect(() => {
    if (!sensorDeviceLookup?.size) {
      return;
    }

    setSessions(current => {
      let changed = false;
      const next = { ...current };

      Object.values(current).forEach(session => {
        const cached = getCachedSessionConfiguration(
          queryClient,
          session.sessionId
        );
        if (!cached) {
          return;
        }

        const sensorFields = resolveSessionSensorFields(
          cached,
          sensorDeviceLookup
        );
        if (
          sensorFields.deviceName === session.deviceName &&
          sensorFields.deviceSensorName === session.deviceSensorName
        ) {
          return;
        }

        next[session.sessionId] = {
          ...session,
          deviceName: sensorFields.deviceName,
          deviceSensorName: sensorFields.deviceSensorName,
        };
        changed = true;
      });

      return changed ? next : current;
    });
  }, [queryClient, sensorDeviceLookup]);

  const sessionList = useMemo(
    () =>
      Object.values(sessions).sort(
        (left, right) => left.sessionId - right.sessionId
      ),
    [sessions]
  );

  const readySessions = useMemo(
    () =>
      sessionList.filter(
        session =>
          session.status === 'ready' && (session.events?.length ?? 0) > 0
      ),
    [sessionList]
  );

  const isLoading = sessionList.some(session => session.status === 'loading');

  return {
    sessions,
    sessionList,
    readySessions,
    isLoading,
    hydrateSessions,
    parsePendingSessionEvents,
  };
};
